// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Video from "@rpsc/App/Components/Media/Video";
import { XNGAGE_CUSTOM_WIDGET } from "@rpsc/XDL/constants";
import React, { useEffect, useState } from "react";
import YouTube, { YouTubeProps } from "react-youtube";
import styled, { css } from "styled-components";

const enum fields {
    videoType = "videoType",
    videoURL = "videoURL",
    videoParameters = "videoParameters",
    title = "title",
    description = "description",
    uploadDate = "uploadDate",
    duration = "duration",
}

interface YoutubeVideoProps extends WidgetProps {
    fields: {
        [fields.videoType]: string;
        [fields.videoURL]: string;
        [fields.videoParameters]: string;
        [fields.title]: string;
        [fields.description]: string;
        [fields.uploadDate]: string;
        [fields.duration]: string;
    };
}

export const videoWrapperCommonStyle = css`
    position: relative;
    padding-bottom: 56.25%;
    padding-top: 0;
    height: 0;
    overflow: hidden;

    iframe,
    object,
    embed {
        position: absolute;
        top: 0;
        left: 0;
    }
`;

const VimeoVideoWrapper = styled.div`
    ${videoWrapperCommonStyle}
`;

const YoutubeVideoWrapper = styled.div`
    ${videoWrapperCommonStyle}
`;

const videoTemplateWidget = ({
    fields: { videoType, videoURL, videoParameters, title, description, uploadDate, duration },
}: YoutubeVideoProps) => {
    const [videoId, setVideoId] = useState("");

    const getYouTubeId = (url: string) => {
        const array = url.split(/(vi\/|v=|\/v\/|youtu\.be\/|\/embed\/)/);
        const Id = array[2] !== undefined ? array[2].split(/[^0-9a-z_\-]/i)[0] : array[0];
        setVideoId(Id);
    };

    const getVimeoId = (url: string) => {
        let Id = "";
        try {
            Id = new URL(url)?.pathname.match(/[0-9]/g)?.join("") || "";
        } catch {
            Id = url.match(/[0-9]/g)?.join("") || "";
        }
        setVideoId(Id);
    };

    useEffect(() => {
        videoType === "youtube" ? getYouTubeId(videoURL) : getVimeoId(videoURL);
    }, [videoType, videoURL]);

    const params: any = parseQueryString(videoParameters);
    const { width, height, ...rest } = params || {};
    const opts = {
        height: height || "100%",
        width: width || "100%",
        playerVars: {
            ...rest,
            playsinline: 1,
        },
    };

    const onYoutubeReady: YouTubeProps["onReady"] = e => {
        if (rest.autoplay === "1") {
            setTimeout(() => {
                e?.target?.playVideo?.();
            }, 1000);
        }
    };

    return (
        <>
            {videoType === "youtube" ? (
                <YoutubeVideoWrapper>
                    <YouTube videoId={videoId} opts={opts} onReady={onYoutubeReady} />
                    <div itemScope itemType="https://schema.org/VideoObject">
                        <div itemID="https://www.youtube.com" itemType="https://schema.org/WebSite">
                            <meta itemProp="url" content={`https://www.youtube.com/embed/${videoId}`} />
                            <meta itemProp="contentUrl" content={`https://www.youtube.com/embed/${videoId}`} />
                            <meta
                                itemProp="thumbnailUrl"
                                content={`http://img.youtube.com/vi/${videoId}/maxresdefault.jpg`}
                            />
                            <meta itemProp="name" content={title} />
                            <meta itemProp="description" content={description} />
                            <meta itemProp="caption" content={description} />
                            <meta itemProp="uploadDate" content={uploadDate} />
                            <meta itemProp="duration" content={duration} />
                        </div>
                    </div>
                </YoutubeVideoWrapper>
            ) : videoType === "vimeo" ? (
                <VimeoVideoWrapper>
                    <Video
                        src={`https://player.vimeo.com/video/${videoId}?color=ff214f&byline=0&portrait=0${videoParameters}`}
                        controls
                    />

                    <div itemScope itemType="https://schema.org/VideoObject">
                        <div itemID="https://www.vimeo.com" itemType="https://schema.org/WebSite">
                            <meta itemProp="url" content={`https://vimeo.com/${videoId}`} />
                            <meta itemProp="contentUrl" content={`https://vimeo.com/${videoId}`} />
                            <meta itemProp="thumbnailUrl" content={`https://vumbnail.com/${videoId}.jpg`} />
                            <meta itemProp="name" content={title} />
                            <meta itemProp="description" content={description} />
                            <meta itemProp="caption" content={description} />
                            <meta itemProp="uploadDate" content={uploadDate} />
                            <meta itemProp="duration" content={duration} />
                        </div>
                    </div>
                </VimeoVideoWrapper>
            ) : (
                <>
                    <Video src={videoURL} controls />
                    <div itemScope itemType="https://schema.org/VideoObject">
                        <div itemID={videoURL} itemType="https://schema.org/WebSite">
                            <meta itemProp="url" content={videoURL} />
                            <meta itemProp="contentUrl" content={videoURL} />
                            <meta itemProp="thumbnailUrl" content={`${videoURL}.jpg`} />
                            <meta itemProp="name" content={title} />
                            <meta itemProp="description" content={description} />
                            <meta itemProp="caption" content={description} />
                            <meta itemProp="uploadDate" content={uploadDate} />
                            <meta itemProp="duration" content={duration} />
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

const metadataMsg = "will be used to add metadata for this video.";

export const definition: WidgetDefinition = {
    group: XNGAGE_CUSTOM_WIDGET,
    fieldDefinitions: [
        {
            name: fields.videoType,
            displayName: "Video Type",
            editorTemplate: "DropDownField",
            defaultValue: "youtube",
            options: [
                {
                    displayName: "Youtube",
                    value: "youtube",
                },
                {
                    displayName: "Vimeo",
                    value: "vimeo",
                },
                {
                    displayName: "MP4",
                    value: "mp4",
                },
            ],
            fieldType: "Translatable",
            isRequired: true,
        },
        {
            name: fields.videoURL,
            displayName: "Video URL or Id",
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "Translatable",
            isRequired: true,
        },
        {
            name: fields.videoParameters,
            displayName: "Video Parameters",
            editorTemplate: "TextField",
            defaultValue: "",
            tooltip: `e.g: "start=1150&other=value&another=value"
             note: autoplay will work only if muted`,
            fieldType: "Translatable",
            isRequired: false,
        },
        {
            name: fields.title,
            displayName: "Title",
            editorTemplate: "MultilineTextField",
            defaultValue: "",
            fieldType: "Translatable",
            isRequired: false,
            tooltip: metadataMsg,
        },
        {
            name: fields.description,
            displayName: "Description",
            editorTemplate: "MultilineTextField",
            defaultValue: "",
            fieldType: "Translatable",
            isRequired: false,
            tooltip: metadataMsg,
        },
        {
            name: fields.uploadDate,
            displayName: "Upload Date",
            editorTemplate: "DateTimeField",
            defaultValue: new Date(),
            fieldType: "Translatable",
            isRequired: false,
            tooltip: metadataMsg,
        },
        {
            name: fields.duration,
            displayName: "Duration",
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "Translatable",
            isRequired: false,
            tooltip: metadataMsg,
        },
    ],
};

const widgetModule: WidgetModule = {
    component: videoTemplateWidget,
    definition,
};

export default widgetModule;
